// src/routes/JobPostCreation/OverallScoreBanner.js

import React from 'react';
import { Box, Typography, CircularProgress, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import { getScoreColor, getScoreGradient } from '../../utils/scoreUtils';
import { bg, text as textColors, border, overlay, statusBg, statusBorder } from '../../utils/darkModeAdapter';

const getCompetitivenessInfo = (level) => {
  switch (level) {
    case 'excellent':
      return { label: '非常に競争力が高い', color: '#4CAF50', icon: <CheckCircleIcon sx={{ fontSize: 20 }} /> };
    case 'good':
      return { label: '競争力あり', color: '#2196F3', icon: <CheckCircleIcon sx={{ fontSize: 20 }} /> };
    case 'average':
      return { label: '平均的', color: '#FF9800', icon: <InfoIcon sx={{ fontSize: 20 }} /> };
    case 'needsWork':
    default:
      return { label: '改善推奨', color: '#f44336', icon: <WarningIcon sx={{ fontSize: 20 }} /> };
  }
};

/**
 * 総合スコアバナー - 大きく目立つデザイン
 */
function OverallScoreBanner({ analysis, isLoading, aiAnalysis, isAiLoading }) {
  const theme = useTheme();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 3,
          px: 3,
          mb: 2,
          borderRadius: 3,
          backgroundColor: overlay(theme).lightest,
          border: `1px solid ${border(theme).default}`,
        }}
      >
        <CircularProgress size={24} sx={{ mr: 1.5 }} />
        <Typography variant="body1" color="text.secondary">
          品質を分析中...
        </Typography>
      </Box>
    );
  }

  if (!analysis) return null;

  const score = analysis.overallScore || 0;
  const competitiveness = analysis.competitiveness || 'needsWork';
  const scoreColor = getScoreColor(score);
  const scoreGradient = getScoreGradient(score);
  const compInfo = getCompetitivenessInfo(competitiveness);

  const strengths = aiAnalysis?.strengths || [];
  const criticalIssues = aiAnalysis?.criticalIssues || [];

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: -10, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      sx={{
        py: 2.5,
        px: 3,
        mb: 2,
        borderRadius: 3,
        backgroundColor: bg(theme).card,
        border: `2px solid ${scoreColor}50`,
        boxShadow: `0 4px 20px ${scoreColor}20`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
          {/* 大きなスコア円 */}
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress
              variant="determinate"
              value={score}
              size={72}
              thickness={5}
              sx={{
                color: scoreColor,
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                },
              }}
            />
            <CircularProgress
              variant="determinate"
              value={100}
              size={72}
              thickness={5}
              sx={{
                color: border(theme).default,
                position: 'absolute',
                left: 0,
                zIndex: -1,
              }}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: '1.4rem',
                  color: scoreColor,
                  lineHeight: 1,
                }}
              >
                {score}
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: textColors(theme).primary, lineHeight: 1.3 }}>
              品質スコア
            </Typography>
            <Typography variant="body2" sx={{ color: textColors(theme).muted, mt: 0.3 }}>
              {analysis.dataSource || '日本市場ベンチマーク'}
            </Typography>
          </Box>
        </Box>

        <Chip
          icon={compInfo.icon}
          label={compInfo.label}
          sx={{
            height: 36,
            backgroundColor: `${compInfo.color}12`,
            color: compInfo.color,
            fontWeight: 700,
            fontSize: '0.9rem',
            border: `1.5px solid ${compInfo.color}35`,
            '& .MuiChip-icon': {
              color: compInfo.color,
            },
            '& .MuiChip-label': {
              px: 1.5,
            },
          }}
        />
      </Box>

      {/* スコアの見方ガイド */}
      <Box sx={{ display: 'flex', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
        {[
          { color: '#4CAF50', label: '80以上: 良好' },
          { color: '#FF9800', label: '60-79: 改善余地あり' },
          { color: '#f44336', label: '60未満: 要改善' },
        ].map(item => (
          <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color }} />
            <Typography variant="caption" color="text.secondary">{item.label}</Typography>
          </Box>
        ))}
      </Box>

      {/* AI分析ローディング */}
      {isAiLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, pt: 1.5, borderTop: `1px solid ${border(theme).light}` }}>
          <CircularProgress size={14} sx={{ color: '#9CA3AF' }} />
          <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
            AI分析中...
          </Typography>
        </Box>
      )}

      {/* AI総合コメント */}
      {aiAnalysis?.overallAssessment && !isAiLoading && (
        <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${border(theme).light}` }}>
          <Typography variant="body2" sx={{ color: textColors(theme).secondary, fontSize: '0.85rem', lineHeight: 1.6 }}>
            {aiAnalysis.overallAssessment}
          </Typography>

          {(strengths.length > 0 || criticalIssues.length > 0) && (
            <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
              {strengths.slice(0, 3).map((s, i) => (
                <Chip
                  key={`s-${i}`}
                  label={s}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    backgroundColor: statusBg(theme).success,
                    color: '#16A34A',
                    border: `1px solid ${statusBorder(theme).success}`,
                  }}
                />
              ))}
              {criticalIssues.slice(0, 3).map((c, i) => (
                <Chip
                  key={`c-${i}`}
                  label={c}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    backgroundColor: statusBg(theme).error,
                    color: '#DC2626',
                    border: `1px solid ${statusBorder(theme).error}`,
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

export default OverallScoreBanner;
